import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { taxApi, elsterApi, type TaxReturnEntry, type ElsterCertificate, type ElsterPreview } from '../../../lib/api';
import { Button, ErrorNote } from '../../ui';
import { useElsterLicense, isLicenseRequired } from './LicenseGate';
import { ApiError, yearUnlockApi } from '../../../lib/api';
import { ReadinessBanner } from './ReadinessBanner';
import { SubmissionHistory, downloadBlob } from './SubmissionHistory';

type ReturnType = 'ustva' | 'ustJahr' | 'est' | 'gewst' | 'euer';

interface Props {
  returnType: ReturnType;
  period: string;
  companyId: string;
  data?: Record<string, unknown>;
  /** Wird nach Validierung/Uebermittlung aufgerufen (zum Historie-Refresh). */
  onChange?: () => void;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  draft:      { label: 'Entwurf',     cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  validated:  { label: 'Geprüft',     cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  submitting: { label: 'Wird übermittelt', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  submitted:  { label: 'Übermittelt', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  accepted:   { label: 'Angenommen',  cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  rejected:   { label: 'Abgelehnt',   cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

function StatusBadge({ status }: { status: string }): ReactElement {
  const s = STATUS_LABELS[status] ?? { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>
  );
}

// ---- Copy button with transient "Kopiert" state ---------------------------

function CopyButton({ text, label = 'Kopieren' }: { text: string; label?: string }): ReactElement {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors in restricted environments
    }
  }

  return (
    <button
      type="button"
      onClick={() => { void handleCopy(); }}
      className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium ring-1 ring-current hover:opacity-80"
    >
      {copied ? 'Kopiert ✓' : label}
    </button>
  );
}

// ---- Submit error panel ---------------------------------------------------

interface SubmitFailureProps {
  errors: { message: string }[];
  supportRef?: string;
  returnCode?: number;
  returnType: ReturnType;
  period: string;
}

const RETURN_TYPE_LABELS: Record<ReturnType, string> = {
  ustva:   'USt-Voranmeldung',
  ustJahr: 'Umsatzsteuer (Jahr)',
  est:     'Einkommensteuer',
  gewst:   'Gewerbesteuer',
  euer:    'EÜR',
};

function SubmitFailurePanel({ errors, supportRef, returnCode, returnType, period }: SubmitFailureProps): ReactElement {
  function buildSupportText(): string {
    const lines: string[] = [];
    if (supportRef) lines.push(`Referenz: ${supportRef}`);
    if (returnCode != null) lines.push(`Rückgabecode: ${returnCode}`);
    lines.push(`Steuerart: ${RETURN_TYPE_LABELS[returnType]} – Zeitraum: ${period}`);
    if (errors.length > 0) {
      lines.push('');
      lines.push('Fehlermeldungen:');
      errors.forEach((e) => lines.push(`  • ${e.message}`));
    }
    return lines.join('\n');
  }

  const mailtoSubject = supportRef
    ? `ELSTER-Fehler ${supportRef}`
    : `ELSTER-Fehler ${RETURN_TYPE_LABELS[returnType]} ${period}`;
  const mailtoHref = `mailto:info@saldify.de?subject=${encodeURIComponent(mailtoSubject)}`;

  return (
    <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800/50 dark:bg-red-950/20">
      <p className="mb-3 font-semibold text-red-800 dark:text-red-300">
        Die Übermittlung war nicht erfolgreich.
      </p>

      {errors.length > 0 && (
        <ul className="mb-3 space-y-1.5">
          {errors.map((err, i) => (
            <li key={i} className="rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
              {err.message}
            </li>
          ))}
        </ul>
      )}

      {supportRef && (
        <div className="mb-3 rounded-lg bg-white/60 p-3 dark:bg-gray-900/40">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Referenz für den Support:
            </span>
            <span className="font-mono text-sm font-bold text-gray-900 dark:text-gray-100">{supportRef}</span>
            {returnCode != null && (
              <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Code {returnCode}
              </span>
            )}
          </div>
          <div className="mt-2">
            <CopyButton text={buildSupportText()} label="Support-Info kopieren" />
          </div>
        </div>
      )}

      <p className="text-sm text-red-700 dark:text-red-400">
        Bitte senden Sie diese Referenz an unseren Support:{' '}
        <a href={mailtoHref} className="font-semibold underline hover:opacity-80">
          info@saldify.de
        </a>
      </p>
    </div>
  );
}

// ---- Validate error panel -------------------------------------------------

function ValidateErrorPanel({ errors }: { errors: { message: string }[] }): ReactElement {
  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/50 dark:bg-amber-950/20">
      <p className="mb-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
        Folgende Punkte beanstandet ELSTER:
      </p>
      <ul className="space-y-1.5">
        {errors.map((err, i) => (
          <li key={i} className="rounded-lg bg-amber-100 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            {err.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Eine Zeile der Vorschau. Positionen mit hinterlegten SKR-Konten lassen sich
 * aufklappen — so ist pruefbar, ob alles richtig zugeordnet ist, und der Wert
 * laesst sich gegen die eigene Summen-/Saldenliste abgleichen.
 */
function PreviewRow({ row }: { row: ElsterPreview['sections'][number]['rows'][number] }): ReactElement {
  const [open, setOpen] = useState(false);
  const hasAccounts = Boolean(row.accounts?.length);

  return (
    <div className="py-1.5">
      <div
        className={`flex items-baseline justify-between gap-4 ${hasAccounts ? 'cursor-pointer' : ''}`}
        onClick={hasAccounts ? () => setOpen((v) => !v) : undefined}
      >
        <dt className={`text-sm ${row.indent ? 'pl-4 text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
          {hasAccounts && (
            <span className="mr-1 inline-block w-3 text-xs text-gray-400">{open ? '▾' : '▸'}</span>
          )}
          {row.label}
        </dt>
        <dd className="shrink-0 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
          {row.value}
        </dd>
      </div>
      {open && row.accounts && (
        <div className="mt-1 space-y-0.5 border-l-2 border-blue-200 pl-3 dark:border-blue-900">
          {row.accounts.map((a) => (
            <div key={a.number} className="flex items-baseline justify-between gap-4">
              <span className="text-xs text-blue-700 dark:text-blue-300">
                {a.number}{a.name ? ` – ${a.name}` : ''}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-blue-700 dark:text-blue-300">
                {a.value} S
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Vorschau des zu uebermittelnden Datensatzes. Zeigt die Werte in der Sprache
 * des Vordrucks; das Roh-XML ist eingeklappt (fuer Steuerberater/Support).
 */
function PreviewDialog({ preview, onClose }: { preview: ElsterPreview; onClose: () => void }): ReactElement {
  const [showXml, setShowXml] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white dark:bg-gray-800">
        <div className="border-b border-gray-200 p-5 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Vorschau der Übermittlung
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Diese Werte gehen für {preview.period} ans Finanzamt. Bitte prüfen, bevor du übermittelst.
          </p>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {preview.sections.length === 0 && (
            <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              Für diese Erklärungsart gibt es noch keine aufbereitete Vorschau. Die technischen
              Daten unten zeigen, was übermittelt wird.
            </p>
          )}
          {preview.sections.map((s) => (
            <div key={s.title} className="mb-5 last:mb-0">
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{s.title}</h3>
              <dl className="divide-y divide-gray-100 dark:divide-gray-700">
                {s.rows.map((r, i) => (
                  <PreviewRow key={`${r.label}-${i}`} row={r} />
                ))}
              </dl>
            </div>
          ))}

          <button
            onClick={() => setShowXml((v) => !v)}
            className="mt-2 text-xs text-gray-500 hover:underline dark:text-gray-400"
          >
            {showXml ? 'Technische Daten ausblenden' : 'Technische Daten (XML) anzeigen'}
          </button>
          {showXml && (
            <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-gray-900 dark:text-gray-400">
              {preview.xml}
            </pre>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 dark:border-gray-700">
          <Button onClick={onClose}>Schließen</Button>
        </div>
      </div>
    </div>
  );
}

// ---- Main component -------------------------------------------------------

export function ElsterSubmit({ returnType, period, companyId, data, onChange }: Props): ReactElement {
  const { ensureAccepted, Modal: LicenseModal } = useElsterLicense(companyId);

  const [entry, setEntry] = useState<TaxReturnEntry | null>(null);
  const [validateErrors, setValidateErrors] = useState<{ message: string }[]>([]);
  const [validateOk, setValidateOk] = useState(false);
  const [preview, setPreview] = useState<ElsterPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savingOrValidating, setSavingOrValidating] = useState(false);
  const [apiError, setApiError] = useState<unknown>(null);

  // Step B: dialog state
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [certificates, setCertificates] = useState<ElsterCertificate[]>([]);
  const [configured, setConfigured] = useState(true);
  const [certId, setCertId] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Gesperrtes Steuerjahr: statt nur abzulehnen gleich den Kauf anbieten.
  const [gesperrt, setGesperrt] = useState<{ jahr: number; preisCents: number } | null>(null);
  const [kaufBusy, setKaufBusy] = useState(false);
  const [ready, setReady] = useState(true); // ELSTER-Voraussetzungen erfuellt?

  // Failure details for submit step
  const [submitFailure, setSubmitFailure] = useState<{
    errors: { message: string }[];
    supportRef?: string;
    returnCode?: number;
  } | null>(null);
  /** Erfolgreiche Uebermittlung — traegt das Transferticket als Nachweis. */
  const [submitOk, setSubmitOk] = useState<{ transferTicket?: string } | null>(null);
  const [protocolBusy, setProtocolBusy] = useState(false);

  // Load existing return for this period/type to show current status.
  useEffect(() => {
    taxApi.listReturns(companyId).then((entries) => {
      const found = entries.find((e) => e.type === returnType && e.period === period);
      if (found) setEntry(found);
    }).catch(() => undefined);
  }, [companyId, returnType, period]);

  async function handleSaveAndValidate() {
    // Gate: ensure license is accepted before any ELSTER action.
    const ok = await ensureAccepted();
    if (!ok) return;

    setApiError(null);
    setValidateErrors([]);
    setValidateOk(false);
    setSubmitFailure(null);
    setSavingOrValidating(true);
    try {
      const saved = await taxApi.saveReturn(companyId, returnType, period, data);
      setEntry(saved.entry);
      const result = await taxApi.validateReturn(companyId, saved.entry.id);
      setValidateErrors(result.errors);
      setValidateOk(result.ok);
      if (result.ok) {
        // Refresh entry (status may have changed to 'validated').
        const entries = await taxApi.listReturns(companyId);
        const found = entries.find((e) => e.id === saved.entry.id);
        if (found) setEntry(found);
      }
    } catch (e) {
      // Jahr nicht freigeschaltet -> Kaufdialog statt Fehlermeldung.
      if (e instanceof ApiError && e.code === 'JAHR_GESPERRT') {
        const d = e.data as { jahr?: number; preisCents?: number } | undefined;
        setShowSubmitDialog(false);
        setGesperrt({ jahr: d?.jahr ?? 0, preisCents: d?.preisCents ?? 0 });
      } else 
      if (isLicenseRequired(e)) {
        // Re-trigger the license modal if the backend explicitly rejects.
        const accepted = await ensureAccepted();
        if (accepted) void handleSaveAndValidate();
      } else {
        setApiError(e);
      }
    } finally {
      setSavingOrValidating(false);
    }
  }

  async function openSubmitDialog() {
    // Gate: ensure license is accepted before opening the submit dialog.
    const ok = await ensureAccepted();
    if (!ok) return;

    setApiError(null);
    setSubmitFailure(null);
    try {
      const res = await elsterApi.listCertificates(companyId);
      setCertificates(res.certificates);
      setConfigured(res.configured);
      if (res.certificates.length > 0 && res.certificates[0]) {
        setCertId(res.certificates[0].id);
      }
    } catch (e) {
      if (isLicenseRequired(e)) {
        const accepted = await ensureAccepted();
        if (accepted) void openSubmitDialog();
        return;
      }
      setApiError(e);
      return;
    }
    setConfirmed(false);
    setPin('');
    setShowSubmitDialog(true);
  }

  async function handleSubmit() {
    if (!entry || !confirmed || !certId || !pin) return;
    setApiError(null);
    setSubmitFailure(null);
    setSubmitting(true);
    try {
      const res = await taxApi.submitReturn(companyId, entry.id, certId, pin);
      if (res.ok) {
        setEntry(res.entry);
        setShowSubmitDialog(false);
        // Transferticket merken: es ist der Beleg dafuer, dass die Erklaerung
        // beim Finanzamt angekommen ist.
        setSubmitOk({ ...(res.transferTicket ? { transferTicket: res.transferTicket } : {}) });
      } else {
        // Extract supportRef / returnCode from the response shape.
        const anyRes = res as unknown as { supportRef?: string; returnCode?: number; errors: { message: string }[] };
        setSubmitFailure({
          errors: res.errors,
          ...(anyRes.supportRef !== undefined ? { supportRef: anyRes.supportRef } : {}),
          ...(anyRes.returnCode !== undefined ? { returnCode: anyRes.returnCode } : {}),
        });
      }
    } catch (e) {
      if (isLicenseRequired(e)) {
        setShowSubmitDialog(false);
        const accepted = await ensureAccepted();
        if (accepted) void openSubmitDialog();
      } else {
        setApiError(e);
      }
    } finally {
      setSubmitting(false);
      onChange?.(); // Historie aktualisieren (egal ob Erfolg oder Ablehnung).
    }
  }

  /** ELSTER-Protokoll zur Uebermittlung herunterladen (Nachweis fuer die Akte). */
  async function handleDownloadProtocol() {
    if (!entry) return;
    setProtocolBusy(true);
    setApiError(null);
    try {
      const blob = await taxApi.protocolPdf(companyId, entry.id);
      downloadBlob(blob, `ELSTER-Protokoll-${entry.type}-${entry.period}.pdf`);
    } catch (e) {
      setApiError(e);
    } finally {
      setProtocolBusy(false);
    }
  }

  async function openPreview() {
    if (!entry) return;
    setPreviewLoading(true);
    setApiError(null);
    try {
      setPreview(await taxApi.previewReturn(companyId, entry.id));
    } catch (e) {
      setApiError(e);
    } finally {
      setPreviewLoading(false);
    }
  }

  return (
    <>
      {/* License modal — rendered at the top so it overlays everything */}
      {LicenseModal}

      <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900/40 dark:bg-blue-950/20">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">An Finanzamt übermitteln (ELSTER)</span>
          {entry && <StatusBadge status={entry.status} />}
        </div>

        {/* Voraussetzungs-Check: warnt proaktiv (Steuernummer/Adresse/Zertifikat/Lizenz). */}
        <div className="mt-3">
          <ReadinessBanner companyId={companyId} onReady={setReady} />
        </div>

        {entry?.errorText && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">{entry.errorText}</p>
        )}

        {apiError ? <div className="mt-2"><ErrorNote error={apiError} /></div> : null}

        {/* Step A: Prüfen */}
        {!validateOk && (
          <div className="mt-3">
            <Button onClick={() => { void handleSaveAndValidate(); }} busy={savingOrValidating} disabled={!ready}>
              Speichern &amp; Prüfen
            </Button>
          </div>
        )}

        {/* Validation errors — rich panel */}
        {validateErrors.length > 0 && (
          <ValidateErrorPanel errors={validateErrors} />
        )}

        {/* Erfolgsbestaetigung: ohne sie bleibt nach der PIN-Eingabe unklar,
            ob die Erklaerung tatsaechlich rausgegangen ist. */}
        {(submitOk || entry?.status === 'accepted' || entry?.status === 'submitted') && (
          <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900/40 dark:bg-green-900/25">
            <p className="text-sm font-semibold text-green-800 dark:text-green-200">
              ✓ Übermittelt — die Erklärung liegt beim Finanzamt.
            </p>
            {entry?.submittedAt && (
              <p className="mt-1 text-xs text-green-700/90 dark:text-green-300/90">
                Gesendet am{' '}
                {new Date(entry.submittedAt).toLocaleString('de-DE', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}{' '}
                Uhr
              </p>
            )}
            {(submitOk?.transferTicket ?? entry?.elsterTransferId) && (
              <p className="mt-1 text-xs text-green-700/90 dark:text-green-300/90">
                Transferticket:{' '}
                <span className="select-all font-mono">
                  {submitOk?.transferTicket ?? entry?.elsterTransferId}
                </span>
                <br />
                Dein Nachweis der Übermittlung – bitte aufbewahren.
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => { void handleDownloadProtocol(); }} busy={protocolBusy}>
                Protokoll (PDF)
              </Button>
            </div>
            <p className="mt-2 text-xs text-green-700/80 dark:text-green-300/80">
              Der Bescheid kommt in den nächsten Wochen per Post oder in dein ELSTER-Postfach.
            </p>
          </div>
        )}

        {validateOk && !submitOk && entry?.status !== 'accepted' && entry?.status !== 'submitted' && (
          <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
            Keine Fehler gefunden — bereit zur Übermittlung.
          </p>
        )}

        {/* Step B: Übermitteln — mit Vorschau, damit vor dem Absenden
            nachvollziehbar ist, welche Werte ans Finanzamt gehen. */}
        {validateOk && !showSubmitDialog && entry?.status !== 'submitted' && entry?.status !== 'accepted' && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={() => { void openSubmitDialog(); }}>An Finanzamt übermitteln</Button>
            <Button variant="secondary" onClick={() => { void openPreview(); }} busy={previewLoading}>
              Vorschau
            </Button>
          </div>
        )}

        {preview && (
          <PreviewDialog preview={preview} onClose={() => setPreview(null)} />
        )}

        {/* Submit dialog */}
        {/* Steuerjahr nicht freigeschaltet: direkt den Kauf anbieten statt
            nur abzulehnen. */}
        {gesperrt && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Steuerjahr {gesperrt.jahr} freischalten
            </p>
            <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-300/90">
              Das laufende Jahr und das Vorjahr sind in deinem Tarif enthalten. Für ältere
              Jahre fällt eine einmalige Freischaltung an – danach kannst du für {gesperrt.jahr}
              beliebig oft übermitteln und korrigieren.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button
                busy={kaufBusy}
                onClick={async () => {
                  setKaufBusy(true);
                  try {
                    const r = await yearUnlockApi.kaufen(companyId, gesperrt.jahr);
                    window.location.href = r.url;
                  } catch (e) {
                    setApiError(e);
                    setKaufBusy(false);
                  }
                }}
              >
                Für {(gesperrt.preisCents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} freischalten
              </Button>
              <button
                onClick={() => setGesperrt(null)}
                className="text-xs text-gray-500 hover:underline dark:text-gray-400"
              >
                abbrechen
              </button>
            </div>
          </div>
        )}

        {showSubmitDialog && (
          <div className="mt-4 space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Übermittlung bestätigen</h3>

            {!configured ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ELSTER ist noch nicht freigeschaltet. Bitte lade zunächst ein Zertifikat in den{' '}
                <Link to="/app/einstellungen" className="font-semibold text-blue-600 hover:underline dark:text-blue-400">
                  Einstellungen
                </Link>{' '}
                hoch.
              </p>
            ) : (
              <>
                {/* Certificate dropdown */}
                {certificates.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Kein ELSTER-Zertifikat vorhanden.{' '}
                    <Link to="/app/einstellungen" className="font-semibold text-blue-600 hover:underline dark:text-blue-400">
                      Jetzt hochladen
                    </Link>
                  </p>
                ) : (
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Zertifikat
                    </span>
                    <select
                      value={certId}
                      onChange={(e) => setCertId(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    >
                      {certificates.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}{c.subject ? ` (${c.subject})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {/* Zertifikats-Passwort */}
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Zertifikats-Passwort
                  </span>
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Passwort Ihrer ELSTER-Zertifikatsdatei"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <span className="mt-1 block text-xs text-gray-400">
                    Das Passwort, das Sie beim Erstellen Ihrer Zertifikatsdatei (.pfx) im ELSTER-Portal vergeben haben – nicht Ihr Portal-Login.
                  </span>
                </label>

                {/* Confirmation checkbox */}
                <label className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    Ich habe die Daten geprüft und übermittle verbindlich in eigener Verantwortung an das Finanzamt.
                  </span>
                </label>

                {apiError ? <ErrorNote error={apiError} /> : null}

                {/* Submission failure panel (within the dialog) */}
                {submitFailure && (
                  <SubmitFailurePanel
                    errors={submitFailure.errors}
                    returnType={returnType}
                    period={period}
                    {...(submitFailure.supportRef !== undefined ? { supportRef: submitFailure.supportRef } : {})}
                    {...(submitFailure.returnCode !== undefined ? { returnCode: submitFailure.returnCode } : {})}
                  />
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={() => { void handleSubmit(); }}
                    busy={submitting}
                    disabled={!confirmed || !certId || !pin}
                  >
                    Jetzt übermitteln
                  </Button>
                  <Button variant="ghost" onClick={() => setShowSubmitDialog(false)}>
                    Abbrechen
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---- Kombiniertes Panel: Uebermittlung + Historie mit Auto-Refresh ---------

/**
 * Buendelt ElsterSubmit + SubmissionHistory mit gemeinsamem refreshKey: nach
 * einer Uebermittlung laedt die Historie sofort neu (kein Reload noetig).
 */
export function ElsterPanel(props: Props & { historyType?: string }): ReactElement {
  const { historyType, ...submitProps } = props;
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <>
      <ElsterSubmit {...submitProps} onChange={() => setRefreshKey((k) => k + 1)} />
      <SubmissionHistory
        companyId={props.companyId}
        {...(historyType ? { type: historyType } : {})}
        refreshKey={refreshKey}
      />
    </>
  );
}
